import { FieldType, type FormField, FormSection as FormSectionType } from "@aam/shared";
import { Card, CardHeader, CardTitle } from "./ui/Card.js";
import { cn } from "../lib/cn.js";

export interface ProfileFieldRow {
  id: string;
  label: string;
  value: string;
  copyValue?: string;
  sensitive?: boolean;
  downloadFileId?: string;
}

export function ProfileSectionCard({
  title,
  rows,
  onCopy,
  copiedId,
  onReveal,
  revealedIds,
  revealingIds,
  revealedValues,
  onDownloadFile,
}: {
  title: string;
  rows: ProfileFieldRow[];
  onCopy?: (id: string, value: string) => void;
  copiedId?: string | null;
  onReveal?: (id: string) => void;
  revealedIds?: Set<string>;
  revealingIds?: Set<string>;
  /** Decrypted values fetched on demand for a "Show" click, keyed by field id. */
  revealedValues?: Map<string, string>;
  onDownloadFile?: (fileId: string, fileName: string) => void;
}) {
  if (rows.length === 0) return null;

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">{title}</CardTitle>
      </CardHeader>
      <dl className="divide-y divide-border-subtle">
        {rows.map((row) => {
          const revealed = revealedIds?.has(row.id);
          const revealing = revealingIds?.has(row.id);
          const revealedValue = revealedValues?.get(row.id);
          const display = row.sensitive && !revealed
            ? "••••••••"
            : row.sensitive && revealing
              ? "Loading…"
              : row.sensitive && revealed
                ? revealedValue ?? (row.value || "—")
                : row.value || "—";

          return (
            <div key={row.id} className="flex flex-col sm:flex-row sm:gap-4 py-3 first:pt-0 last:pb-0">
              <dt className="sm:w-40 shrink-0 text-xs font-semibold uppercase tracking-wider text-content-secondary">
                {row.label}
              </dt>
              <dd className="flex-1 flex items-start gap-2 text-sm text-content-primary font-medium min-w-0">
                <span className="break-words flex-1">{display}</span>
                <span className="flex shrink-0 gap-1">
                  {row.sensitive && onReveal && (
                    <button
                      type="button"
                      disabled={revealing}
                      onClick={() => onReveal(row.id)}
                      className="text-xs text-content-muted hover:text-content-primary px-1.5 py-0.5 rounded border border-border-default disabled:opacity-50"
                    >
                      {revealing ? "Loading…" : revealed ? "Hide" : "Show"}
                    </button>
                  )}
                  {row.downloadFileId && onDownloadFile && (
                    <button
                      type="button"
                      onClick={() => onDownloadFile(row.downloadFileId!, row.value)}
                      className="text-xs text-accent hover:opacity-90 px-1.5 py-0.5 rounded border border-accent/30"
                    >
                      Download
                    </button>
                  )}
                  {onCopy && row.copyValue && (
                    <button
                      type="button"
                      onClick={() => onCopy(row.id, row.copyValue!)}
                      className={cn(
                        "text-xs px-1.5 py-0.5 rounded border transition-colors",
                        copiedId === row.id
                          ? "border-status-active-border text-status-active-text"
                          : "border-border-default text-content-muted hover:text-content-primary"
                      )}
                    >
                      {copiedId === row.id ? "Copied" : "Copy"}
                    </button>
                  )}
                </span>
              </dd>
            </div>
          );
        })}
      </dl>
    </Card>
  );
}

/** Build grouped profile cards from form schema sections + formData. */
export function buildProfileSections(
  sections: FormSectionType[],
  formData: Record<string, unknown>,
  options?: {
    visibleFieldIds?: Set<string>;
    formatValue?: (fieldId: string, value: unknown) => string;
    labelFor?: (fieldId: string, field: FormField) => string;
    downloadFileIdFor?: (fieldId: string, value: unknown) => string | undefined;
  }
): Array<{ sectionId: string; title: string; rows: ProfileFieldRow[] }> {
  const visible = options?.visibleFieldIds;
  const formatValue = options?.formatValue ?? ((_, v) => (v == null || v === "" ? "" : String(v)));
  const labelFor = options?.labelFor ?? ((_, f) => f.label);

  return sections
    .map((section) => {
      const rows: ProfileFieldRow[] = [];
      for (const field of section.fields) {
        if (visible && !visible.has(field.id)) continue;
        const raw = formData[field.id];
        if (raw === undefined || raw === null || raw === "") continue;
        rows.push({
          id: field.id,
          label: labelFor(field.id, field),
          value: formatValue(field.id, raw),
          copyValue: formatValue(field.id, raw),
          sensitive: field.encrypted === true || field.type === FieldType.SSN,
          downloadFileId: options?.downloadFileIdFor?.(field.id, raw),
        });
      }
      return { sectionId: section.id, title: section.title, rows };
    })
    .filter((section) => section.rows.length > 0);
}
